import { Popover, PopoverOrigin } from '@mui/material';
import * as React from 'react';
// Styles
import {
  IconWrapper,
  TextWrapper,
  Item,
  Menu,
  StyledMenuProps
} from './PopoverMenu.style';

// Components
import Grid from '../../layoutComponents/Grid';
import { ORDER_ITEM_STATUS } from 'component/MarketPlace/OrderRequestDetail/constant';

export interface IPopoverMenuProps<T = PopoverMenuOptionValue>
  extends StyledMenuProps {
  id?: string;
  options: IPopoverMenuOption<T>[];
  children: JSX.Element;
  disabled?: boolean;
  fullWidth?: boolean;
  data?: any;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  className?: string;
}

type PopoverMenuOptionValue = string | number;

export interface IPopoverMenuOption<T = PopoverMenuOptionValue> {
  /**
   * Display name shown to the user in the menu list
   */
  name: string | number;

  /**
   * Internal code of the menu option
   */
  code: T;

  /**
   * Callback function to be ran when menu option is selected
   */
  onClickFn: (...args: any[]) => void;

  /**
   * Icon to display next to the menu option
   */
  icon?: JSX.Element;
}

interface IPopoverMenuItemProps<T = PopoverMenuOptionValue> {
  /**
   * Object that contains name and code properties of menu item
   */
  option: IPopoverMenuOption<T>;

  /**
   * Index of menu item in selectedItems array
   */
  index: number;
}

function PopoverMenu<T = PopoverMenuOptionValue>({
  id,
  options,
  disabled,
  children,
  fullWidth,
  data,
  anchorOrigin,
  transformOrigin,
  className,
}: IPopoverMenuProps<T>): React.ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /*
   * Renders a single menu option
   */
  // eslint-disable-next-line @typescript-eslint/no-shadow
  function MenuItem<T = PopoverMenuOptionValue>({
    option,
    index
  }: IPopoverMenuItemProps<T>): React.ReactElement {
    const handleOptionClick = (): void => {
      if(!!data){
        if(option.code === ORDER_ITEM_STATUS.APPROVED){
          if(data?.isMarketplaceList){
            option.onClickFn(data?.order_detail?._id, ORDER_ITEM_STATUS.APPROVED, data?.isMarketplaceList);
          }
          else{
            option.onClickFn(data?._id, ORDER_ITEM_STATUS.APPROVED, data?.isMarketplaceList);
          }
        }
        else if(option.code === ORDER_ITEM_STATUS.NOT_APPROVED){
          if(data?.isMarketplaceList){
            option.onClickFn(data?.order_detail?._id, ORDER_ITEM_STATUS.NOT_APPROVED, data?.isMarketplaceList);
          }
          else{
            option.onClickFn(data?._id, ORDER_ITEM_STATUS.NOT_APPROVED, data?.isMarketplaceList);
          }
        }
        else if(data?.isMarketplaceList){
          option.onClickFn(data?.order_detail?._id, data?.order_detail?.userId, data?.name, data?.isMarketplaceList);
        }
        else{
          option.onClickFn(data?._id, data?.requestItem?.variables?.json_storage?.user_sys_id, data?.requestItem?.variables?.json_storage?.requestor_name, data?.isMarketplaceList);
        }
      }
      else{
        option.onClickFn();
      }
      handleClose();
    };

    return (
      <Item
        role="menuitem"
        p={2}
        m={0}
        onClick={handleOptionClick}
        fullWidth={fullWidth}
      >
        <Grid alignItems="center">
          {option.icon && <IconWrapper>{option.icon}</IconWrapper>}
          <TextWrapper>{option.name}</TextWrapper>
        </Grid>
      </Item>
    );
  }

  return (
    <>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          onClick: handleOpen,
          disabled: disabled,
        })
      )}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
          ...anchorOrigin,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
          ...transformOrigin,
        }}
        className={className}
        onClose={handleClose}
      >
        <Menu
          role="menu"
          id={id}
          p={0}
          my={0}
          fullWidth={fullWidth}
        >
          {options.map((option: IPopoverMenuOption<T>, index: number) => {
            return <MenuItem key={index} index={index} option={option} />;
          })}
        </Menu>
      </Popover>
    </>
  );
};

export default PopoverMenu;
